import { useEffect, useState } from 'react';
import { Box, Button, FormattedRelativeTime, InlineStack, Text, Tooltip } from '@customerio/pluma-components/react';
import type { NewItemsStatusState } from '@/hooks/use-load-new';

/** How often the relative time re-reads the clock. */
const CLOCK_TICK_MS = 10_000;

export type NewItemsStatusProps = NewItemsStatusState & {
  /** Name and tooltip of the check-now button, e.g. "Check for new activity". */
  checkNowLabel: string;
};

/**
 * "Checked a few seconds ago ↻" — the toolbar status for a "load new" feed (see `hooks/use-load-new.ts`).
 *
 * - The time is when the page last checked for new entries, relative and updating; hover shows the
 *   full date.
 * - The button checks now and refetches the list, with a tooltip for its purpose. It spins only for
 *   the user's own check or a list load.
 * - A polite live region announces new entries, so screen-reader users hear about the "Load N new"
 *   row. It speaks only when the text changes.
 */
export function NewItemsStatus({
  lastChecked,
  isChecking,
  onCheckNow,
  announcement,
  checkNowLabel,
}: NewItemsStatusProps) {
  // Our own clock, passed as `referenceDate`: the component's built-in clock can lag a check that
  // just finished ("in a few seconds"), and remounting it to catch up would close an open tooltip.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <InlineStack alignBlock="center" gap="100" data-test-new-items-status={isChecking ? 'checking' : 'idle'}>
      <Text size="sm" color="secondary">
        Checked{' '}
        <FormattedRelativeTime
          date={lastChecked}
          referenceDate={Math.max(now, lastChecked)}
          shouldUppercaseFirst={false}
          withTooltip
        />
      </Text>
      {/* `isCustom` makes the button itself the tooltip trigger; a plain Tooltip wraps its child in
          a trigger button of its own, which would nest two buttons with the same name. */}
      <Tooltip isCustom content={checkNowLabel}>
        {(context) => (
          <Button
            {...context.reference.getReferenceProps()}
            ref={context.reference.setReference}
            aria-label={checkNowLabel}
            icon="reset"
            isIconOnly
            isLoading={isChecking}
            onClick={onCheckNow}
            size="sm"
            variant="tertiary"
          />
        )}
      </Tooltip>
      <Box visuallyHidden role="status" aria-live="polite">
        {announcement}
      </Box>
    </InlineStack>
  );
}
